import React from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import FooterSection from "../components/FooterSection";
import PageSEO from "../components/PageSEO";
import "../styles/PaidSearchService.css";

export default function PaidSearchPage() {
  return (
    <div className="paid-search-page">
      <PageSEO
        title="Paid Search Services | Google Ads Management | Sparkdenz"
        description="Drive qualified leads with Sparkdenz paid search services including Google Ads strategy, bid optimization, audience targeting, and conversion-focused reporting."
        canonicalPath="/paid-search"
      />
      <MenuBar />
      <main>
        {/* Hero Section */}
        <section className="service-hero">
          <div className="section-inner">
            <p className="section-tag">Paid Search</p>
            <h1>Turn Clicks Into Customers with Strategic Google Ads</h1>
            <p className="service-hero-text">
              Capture high-intent buyers actively searching for your solutions. Our data-driven 
              Google Ads management delivers qualified leads and maximized ROI with continuous 
              optimization and testing.
            </p>
            <div className="service-hero-actions">
              <Link className="btn primary" to="/contact-us">
                Get Your Free Audit
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Schedule Consultation
              </Link>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="service-details">
          <div className="section-inner">
            <h2>Our Paid Search Services</h2>
            <div className="services-grid">
              <div className="service-item">
                <div className="service-icon">🎯</div>
                <h3>Campaign Strategy & Setup</h3>
                <p>
                  We build comprehensive Google Ads campaigns aligned with your business goals. 
                  From account structure to keyword selection and bid strategy, every element 
                  is optimized for maximum efficiency.
                </p>
                <ul className="service-list">
                  <li>Account audits & restructuring</li>
                  <li>Keyword research & intent mapping</li>
                  <li>Campaign architecture design</li>
                  <li>Bid strategy optimization</li>
                  <li>Budget allocation planning</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📊</div>
                <h3>Ad Copy & Creative Optimization</h3>
                <p>
                  Compelling ad copy drives clicks, but great landing pages drive conversions. 
                  We craft persuasive headlines and descriptions, then test relentlessly to 
                  maximize click-through rates and conversion quality.
                </p>
                <ul className="service-list">
                  <li>High-performing headline creation</li>
                  <li>A/B testing & multivariate testing</li>
                  <li>Ad extension optimization</li>
                  <li>Responsive search ads setup</li>
                  <li>Sitelink & callout extensions</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">💰</div>
                <h3>Bid Management & Automation</h3>
                <p>
                  Smart bidding isn't set-it-and-forget-it. We leverage Google's automation 
                  tools combined with continuous monitoring to ensure your budget stays efficient 
                  while hitting conversion targets.
                </p>
                <ul className="service-list">
                  <li>Target ROAS bidding</li>
                  <li>Target CPA optimization</li>
                  <li>Enhanced CPC management</li>
                  <li>Portfolio bid strategy setup</li>
                  <li>Bid adjustment optimization</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🔍</div>
                <h3>Audience Targeting & Remarketing</h3>
                <p>
                  Reach the right people at the right time with advanced audience targeting. 
                  We build custom audiences, implement remarketing campaigns, and create 
                  lookalike audiences to expand your reach.
                </p>
                <ul className="service-list">
                  <li>In-market audience targeting</li>
                  <li>Remarketing campaigns</li>
                  <li>Lookalike audience creation</li>
                  <li>Custom intent audiences</li>
                  <li>Demographic & device targeting</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📱</div>
                <h3>Mobile & Multi-Device Optimization</h3>
                <p>
                  Mobile searches drive the majority of paid search volume. We optimize every 
                  campaign for mobile-first intent, from ad copy to landing pages, ensuring 
                  seamless user experiences across all devices.
                </p>
                <ul className="service-list">
                  <li>Mobile-first campaign design</li>
                  <li>Click-to-call tracking</li>
                  <li>Mobile landing page optimization</li>
                  <li>Device bid adjustments</li>
                  <li>App promotion campaigns</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📈</div>
                <h3>Performance Analytics & Reporting</h3>
                <p>
                  Data drives decisions. We provide detailed performance insights, custom 
                  dashboards, and monthly reports showing ROI, conversion metrics, and 
                  opportunities for growth.
                </p>
                <ul className="service-list">
                  <li>Conversion tracking setup</li>
                  <li>Google Analytics integration</li>
                  <li>Custom dashboard creation</li>
                  <li>Monthly performance reports</li>
                  <li>Competitive benchmarking</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="service-results">
          <div className="section-inner">
            <h2>Proven Results</h2>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-stat">312%</div>
                <p>Average ROI improvement</p>
              </div>
              <div className="result-card">
                <div className="result-stat">47%</div>
                <p>Average CPC reduction</p>
              </div>
              <div className="result-card">
                <div className="result-stat">3.8x</div>
                <p>Average ROAS achieved</p>
              </div>
              <div className="result-card">
                <div className="result-stat">156%</div>
                <p>Average conversion increase</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="section-inner">
            <h2>Our Paid Search Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">01</div>
                <h3>Audit & Analysis</h3>
                <p>We analyze your current campaigns, identify waste, and find optimization opportunities.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">02</div>
                <h3>Strategy & Planning</h3>
                <p>Build a comprehensive Google Ads strategy aligned with your revenue goals.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">03</div>
                <h3>Campaign Implementation</h3>
                <p>Set up optimized campaigns with proper tracking, targeting, and bid strategy.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">04</div>
                <h3>Ongoing Optimization</h3>
                <p>Continuous monitoring, testing, and optimization to maximize ROI.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="section-inner">
            <h2>Ready to Scale with Paid Search?</h2>
            <p>Let's build a Google Ads strategy that drives qualified leads and revenue growth.</p>
            <div className="cta-buttons">
              <Link className="btn primary" to="/contact-us">
                Get Your Free Audit
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Talk to Specialist
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
